import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ImageDTO, ImageListQuery } from "@shared/types";
import { api, ApiError } from "../lib/api";
import { useToast } from "../context/ToastContext";
import { useSidebar } from "../context/SidebarContext";
import { useAppConfig } from "../hooks/useAppConfig";
import { useFolders } from "../hooks/useFolders";
import { useImages } from "../hooks/useImages";
import { useStats } from "../hooks/useStats";
import { useDeploymentStatus } from "../hooks/useDeploymentStatus";
import { useDebounced } from "../hooks/useDebounced";
import { FolderTree, type FolderSelection } from "../components/FolderTree";
import { Breadcrumbs, type Crumb } from "../components/Breadcrumbs";
import { GridControls } from "../components/GridControls";
import { ImageGrid } from "../components/ImageGrid";
import { ImagePreviewModal } from "../components/ImagePreviewModal";
import { RenameDialog } from "../components/RenameDialog";
import { MoveDialog } from "../components/MoveDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { UploadDialog } from "../components/UploadDialog";
import { StatsBar } from "../components/StatsBar";
import { NewFolderButton } from "../components/NewFolderButton";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";

const PAGE_SIZE = 60;

export function DashboardPage() {
  const toast = useToast();
  const config = useAppConfig();
  const folders = useFolders();
  const { open: sidebarOpen, setOpen: setSidebarOpen, setHasSidebar } = useSidebar();

  const [selection, setSelection] = useState<FolderSelection>("all");
  const [recursive, setRecursive] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const search = useDebounced(rawSearch, 300);
  const [filters, setFilters] = useState<Pick<ImageListQuery, "type" | "sizeBucket" | "sort">>({ sort: "newest" });
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);

  const isRealFolder = selection !== "all" && selection !== "uncategorized";

  // The folder drawer toggle lives in the top nav, so tell it we have one.
  useEffect(() => {
    setHasSidebar(true);
    return () => setHasSidebar(false);
  }, [setHasSidebar]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSidebarOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen, setSidebarOpen]);

  const query = useMemo<ImageListQuery>(
    () => ({
      folderId: selection === "all" ? undefined : selection === "uncategorized" ? null : selection,
      recursive: isRealFolder ? recursive : false,
      q: search.trim() || undefined,
      type: filters.type,
      sizeBucket: filters.sizeBucket,
      sort: filters.sort,
      page,
      pageSize: PAGE_SIZE,
    }),
    [selection, isRealFolder, recursive, search, filters, page],
  );

  const images = useImages(query);
  const { stats, reload: reloadStats } = useStats(refreshTick);
  const { status: deployment, refresh: refreshDeployment } = useDeploymentStatus(true);

  const [preview, setPreview] = useState<ImageDTO | null>(null);
  const [renameTarget, setRenameTarget] = useState<ImageDTO | null>(null);
  const [moveTarget, setMoveTarget] = useState<ImageDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ImageDTO | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>(undefined);
  const [publishing, setPublishing] = useState(false);
  const [windowDragging, setWindowDragging] = useState(false);

  useEffect(() => setPage(1), [selection, search, filters, recursive]);

  const afterMutation = useCallback(() => {
    setRefreshTick((t) => t + 1);
    refreshDeployment();
    void folders.reload();
  }, [refreshDeployment, folders]);

  const prevState = useRef(deployment?.state);
  useEffect(() => {
    const was = prevState.current;
    prevState.current = deployment?.state;
    if ((was === "running" || was === "queued") && deployment?.state === "idle") {
      void images.reload({ quiet: true });
      reloadStats();
      void folders.reload();
    }
  }, [deployment?.state, images, reloadStats, folders]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) void images.reload({ quiet: true });
    }, 30000);
    return () => window.clearInterval(id);
  }, [images]);

  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setWindowDragging(true);
    };
    const onLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) setWindowDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setWindowDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []).filter(
        (f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|avif|bmp|tiff?|ico|svg)$/i.test(f.name),
      );
      if (files.length) {
        setDroppedFiles(files);
        setUploadOpen(true);
      }
    };
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  const crumbs = useMemo<Crumb[]>(() => {
    const base: Crumb[] = [{ label: "All images", onClick: () => setSelection("all") }];
    if (selection === "all") return [{ label: "All images" }];
    if (selection === "uncategorized") return [...base, { label: "Uncategorized" }];
    const folder = folders.folders.find((f) => f.id === selection);
    if (!folder) return base;
    const parts = folder.path.split("/");
    const byPath = new Map(folders.folders.map((f) => [f.path, f]));
    const segCrumbs: Crumb[] = parts.map((_, i) => {
      const p = parts.slice(0, i + 1).join("/");
      const f = byPath.get(p);
      return {
        label: f?.name ?? parts[i],
        onClick: f && i < parts.length - 1 ? () => setSelection(f.id) : undefined,
      };
    });
    return [...base, ...segCrumbs];
  }, [selection, folders.folders]);

  const uploadFolderId = isRealFolder ? (selection as string) : null;

  const publishNow = async () => {
    setPublishing(true);
    try {
      await api.runDeployment();
      refreshDeployment();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not start publish.");
    } finally {
      setPublishing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(images.total / PAGE_SIZE));

  return (
    <div className="flex h-full">
      {/* Below `lg` this is a drawer sitting under the header; from `lg` it is a
          normal column in the flex row. */}
      <aside
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } glass glass-strong fixed bottom-0 left-0 top-14 z-40 flex w-64 max-w-[85vw] flex-col rounded-none border-y-0 border-l-0 px-2.5 py-3 transition-transform duration-200 lg:static lg:z-0 lg:w-60 lg:max-w-none lg:translate-x-0`}
      >
        <FolderTree
          tree={folders.tree}
          selection={selection}
          onSelect={(s) => {
            setSelection(s);
            setSidebarOpen(false);
          }}
          totalImages={stats?.images ?? 0}
          uncategorizedCount={folders.uncategorizedCount}
          onCreate={folders.createFolder}
          onRename={folders.renameFolder}
          onDelete={folders.deleteFolder}
        />
      </aside>
      {sidebarOpen && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-30 bg-zinc-900/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <section className="scrollable flex min-w-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto w-full max-w-[1440px] space-y-4 p-4 sm:p-6">
          <StatsBar stats={stats} deployment={deployment} publishing={publishing} onPublishNow={publishNow} />

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9 3.5a5.5 5.5 0 1 0 3.4 9.83l3.13 3.14a.75.75 0 1 0 1.06-1.06l-3.14-3.14A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" />
              </svg>
              <input
                value={rawSearch}
                onChange={(e) => setRawSearch(e.target.value)}
                placeholder="Search images"
                aria-label="Search images"
                className="field-sm w-full pl-9"
              />
            </div>

            <div className="flex items-center gap-2">
              <NewFolderButton onCreate={folders.createFolder} className="flex-1 sm:flex-none" />
              <Button
                size="sm"
                variant="primary"
                className="flex-1 sm:flex-none"
                onClick={() => {
                  setDroppedFiles(undefined);
                  setUploadOpen(true);
                }}
              >
                <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2.5a1 1 0 0 1 .7.29l3.5 3.5a1 1 0 0 1-1.4 1.42L11 5.9V13a1 1 0 1 1-2 0V5.91L7.2 7.71a1 1 0 0 1-1.4-1.42l3.5-3.5A1 1 0 0 1 10 2.5ZM4 13a1 1 0 0 1 1 1v1.5a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V14a1 1 0 1 1 2 0v1.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 3 15.5V14a1 1 0 0 1 1-1Z" />
                </svg>
                Upload
              </Button>
              <Button
                size="icon"
                variant="secondary"
                onClick={() => void images.reload()}
                title="Refresh"
                aria-label="Refresh"
                className="flex-shrink-0"
              >
                {images.loading ? (
                  <Spinner small />
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 4a6 6 0 1 0 5.65 4H13.5a4 4 0 1 1-.94-4.24L11 5.5h4.5V1L14 2.5A6 6 0 0 0 10 4Z" />
                  </svg>
                )}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Breadcrumbs items={crumbs} />
            <GridControls
              query={query}
              recursiveVisible={isRealFolder}
              onChange={(patch) => {
                if ("recursive" in patch) setRecursive(!!patch.recursive);
                setFilters((f) => ({
                  ...f,
                  type: "type" in patch ? patch.type : f.type,
                  sizeBucket: "sizeBucket" in patch ? patch.sizeBucket : f.sizeBucket,
                  sort: patch.sort ?? f.sort,
                }));
              }}
            />
          </div>

          <p className="text-2xs tabular-nums text-zinc-500">
            {images.total.toLocaleString()} image{images.total === 1 ? "" : "s"}
          </p>

          <ImageGrid
            images={images.images}
            loading={images.loading}
            showPendingBadge={config?.driver === "cloudflare"}
            onPreview={setPreview}
            onRename={setRenameTarget}
            onMove={setMoveTarget}
            onDelete={setDeleteTarget}
            onDetails={setPreview}
            onUploadClick={() => {
              setDroppedFiles(undefined);
              setUploadOpen(true);
            }}
          />

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 py-2 text-[13px]">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span className="whitespace-nowrap tabular-nums text-zinc-600">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>

      {windowDragging && (
        <div className="pointer-events-none fixed inset-0 z-40 grid place-items-center bg-zinc-900/15 p-6 backdrop-blur-sm">
          <div className="glass glass-strong rounded-2xl border-2 border-dashed border-zinc-900/30 px-8 py-8 text-center shadow-glass-lg sm:px-12 sm:py-9">
            <p className="text-sm font-semibold text-zinc-900">Drop to upload</p>
          </div>
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setDroppedFiles(undefined);
          afterMutation();
        }}
        folders={folders.folders}
        defaultFolderId={uploadFolderId}
        initialFiles={droppedFiles}
        config={config}
        onUploaded={(img) => {
          images.prependImages([img]);
          setRefreshTick((t) => t + 1);
          refreshDeployment();
        }}
      />

      <ImagePreviewModal
        image={preview}
        onClose={() => setPreview(null)}
        onRename={(img) => {
          setPreview(null);
          setRenameTarget(img);
        }}
        onMove={(img) => {
          setPreview(null);
          setMoveTarget(img);
        }}
        onDelete={(img) => {
          setPreview(null);
          setDeleteTarget(img);
        }}
      />

      <RenameDialog
        image={renameTarget}
        onClose={() => setRenameTarget(null)}
        onDone={(updated) => {
          images.patchImage(updated);
          afterMutation();
        }}
      />

      <MoveDialog
        image={moveTarget}
        folders={folders.folders}
        onClose={() => setMoveTarget(null)}
        onDone={(updated) => {
          if (isRealFolder && updated.folderId !== selection) images.removeImage(updated.id);
          else images.patchImage(updated);
          afterMutation();
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete image"
        danger
        confirmLabel="Delete"
        body={
          <span>
            Delete <span className="font-medium text-zinc-900">{deleteTarget?.filename}</span>? This permanently removes
            the image and its direct URL.
          </span>
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await api.deleteImage(deleteTarget.id);
            images.removeImage(deleteTarget.id);
            toast.success("Image deleted.");
            afterMutation();
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Could not delete image.");
          } finally {
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}
